import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminFolateVitaminB9Calculator.css'; // Using common CSS file
import '../../styles/components/ainutritionlandingpage.css';
// 1. Folate Fundamentals & DFE Conversion
const dfeFactors = {
    natural_food: 1.0,
    folic_acid_with_food: 1.7, // Fortified food or supplement with meal
    folic_acid_empty_stomach: 2.0, // Supplement on empty stomach
    // Methylfolate DFE is debated, often considered similar to food folate or slightly higher.
    // ODS doesn't assign a DFE. Using 1.7 as a reasonable estimate for bioavailability unless taken fasting.
    methyl_folate: 1.7,
};

// 2. Reference Intakes (IOM DRIs, expressed as DFE µg/day)
const folateRdaTable = { // µg DFE / day
    '0-6m': { rda: 65, isAI: true },
    '7-12m': { rda: 80, isAI: true },
    '1-3y': { rda: 150 },
    '4-8y': { rda: 200 },
    '9-13y': { rda: 300 },
    '14-18y': { rda: 400 },
    '19-50y': { rda: 400 },
    '51+y': { rda: 400 },
    'pregnant': { rda: 600 },
    'lactating': { rda: 500 },
};

// 1. UL (Tolerable Upper Intake Level) - Applies ONLY to SYNTHETIC Folic Acid (µg/day)
const folateUlTable = {
    '0-12m': null, // Not established, source should be breast milk, formula, food
    '1-3y': 300,
    '4-8y': 400,
    '9-13y': 600,
    '14-18y': 800,
    '19+y': 1000,
    'pregnant': 1000,
    'lactating': 1000,
};

// 5. Food folate content (µg NATURAL folate per serving, unless specified)
const foodFolateValues = {
    spinach_raw_1cup: 58, // Natural
    black_eyed_peas_cooked_half_cup: 105, // Natural
    // Using ODS value for 1/2 cup cooked Asparagus
    asparagus_cooked_half_cup: 134, // Natural (ODS)
    avocado_half: 81, // Natural (ODS for half)
    fortified_breakfast_cereal_serving: 100,
    fortified_bread_slice_folic_acid: 60,
    fortified_pasta_rice_cup_folic_acid: 150,
    // Additional Natural Sources
    lentils_cooked_half_cup: 179,
    broccoli_cooked_half_cup: 84,
    orange_medium: 29,
    beef_liver_cooked_3oz: 215,
};


// 5. Absorption Penalties (Fractional reduction)
const absorptionPenalties = {
    alcohol_moderate: 0.10, // 10% reduction
    alcohol_heavy: 0.20, // 20% reduction
    gi_or_med_penalty: 0.15, // 15% reduction for GI conditions / specific meds
};

// 5. MTHFR Multipliers for Target RDA
const mthfrMultipliers = {
    none: 1.0,
    hetero: 1.2, // Suggestion 1.2-1.4x
    homo: 1.4,   // Suggestion 1.2-1.4x
};

// --- Helper Functions ---

// Get Folate RDA key based on age/status
const getRdaFolateKey = (ageNum, isPregnant, isLactating) => {
    if (isPregnant) return 'pregnant';
    if (isLactating) return 'lactating';
    if (ageNum < 0.5) return '0-6m';
    if (ageNum < 1) return '7-12m';
    if (ageNum <= 3) return '1-3y';
    if (ageNum <= 8) return '4-8y';
    if (ageNum <= 13) return '9-13y';
    if (ageNum <= 18) return '14-18y';
    if (ageNum <= 50) return '19-50y';
    if (ageNum > 50) return '51+y';
    return '19-50y'; // Default fallback
};

// Get Folate UL key based on age (synthetic folic acid only)
const getUlFolateKey = (ageNum, isPregnant, isLactating) => {
    // UL values differ for 14-18y vs 19+y during pregnancy/lactation
    if (isPregnant || isLactating) {
        if (ageNum >= 14 && ageNum <= 18) return '14-18y'; // Uses 800 µg UL
        if (ageNum >= 19) return '19+y'; // Uses 1000 µg UL
    }
    // Standard age groups
    if (ageNum < 1) return '0-12m'; // UL not established
    if (ageNum <= 3) return '1-3y';
    if (ageNum <= 8) return '4-8y';
    if (ageNum <= 13) return '9-13y';
    if (ageNum <= 18) return '14-18y';
    if (ageNum >= 19) return '19+y';
    return '19+y'; // Default fallback
};

// --- Component ---
function VitaminFolateVitaminB9Calculator() {
    // --- State for User Inputs ---
    const [age, setAge] = useState('');
    const [sex, setSex] = useState(''); // 'male', 'female'
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);
    const [planningPregnancy, setPlanningPregnancy] = useState(false);

    // Genetics
    const [mthfrStatus, setMthfrStatus] = useState('none'); // 'none', 'hetero', 'homo'

    // Diet (Daily Servings)
    const [dietServings, setDietServings] = useState({
        // Natural Folate Sources (µg natural folate per serving)
        leafyGreens_spinach_1cup: '', // Raw Spinach (58)
        leafyGreens_other_1cup: '', // Placeholder for romaine, etc. ~50 avg
        legumes_beans_peas_lentils_half_cup: '', // Avg ~150 (e.g., lentils 179, black-eyed peas 105)
        citrus_orange_1med: '', // (29)
        avocado_half: '', // (81)
        asparagus_half_cup: '', // (134 cooked)
        broccoli_half_cup: '', // (84 cooked)
        beef_liver_3oz: '', // (215 cooked)
        // Fortified Foods (µg FOLIC ACID per serving)
        fortified_cereal_serving: '', // Number of servings per day
        fortified_cereal_folic_acid_mcg: 100, // User input for µg folic acid per serving
        fortified_bread_slice: '', // Number of slices per day
        fortified_pasta_rice_cup: '', // Cooked cups per day
    });

    // Supplements
    const [takesSupplement, setTakesSupplement] = useState(false);
    const [supplementForm, setSupplementForm] = useState('folic_acid'); // 'folic_acid', 'methyl_folate', 'prenatal'
    const [supplementDoseMcg, setSupplementDoseMcg] = useState(''); // Dose in µg
    const [supplementFrequency, setSupplementFrequency] = useState('daily'); // 'daily', 'alternate_day', 'weekly', 'other'
    const [supplementTiming, setSupplementTiming] = useState('with_food'); // 'with_food', 'empty_stomach'

    // Lifestyle & Health
    const [alcoholIntake, setAlcoholIntake] = useState('none'); // 'none', 'moderate', 'heavy'
    const [hasGiCondition, setHasGiCondition] = useState(false); // Bariatric, IBD, Celiac
    const [takesAntiFolateMeds, setTakesAntiFolateMeds] = useState(false); // Methotrexate, AEDs

    // Labs (Optional)
    const [b12StatusKnownOk, setB12StatusKnownOk] = useState(false); // Check if user confirms B12 is checked and normal
    const [rbcFolateLevel, setRbcFolateLevel] = useState(''); // µg/L or nmol/L - input units needed

    // --- State for Results ---
    const [calculationResult, setCalculationResult] = useState(null);
    const [showResults, setShowResults] = useState(false);

    // --- Input Handlers ---
    const handleDietChange = (event) => {
        const { name, value } = event.target;
        setDietServings(prev => ({ ...prev, [name]: value }));
    };

    // --- Derived State ---
    const ageNum = parseInt(age, 10);
    const isFemale = sex === 'female';

    // Disable pregnancy/lactation based on sex and age (rough)
    useEffect(() => {
        if (!isFemale || ageNum < 12 || ageNum > 55) {
            setIsPregnant(false);
            setIsLactating(false);
            setPlanningPregnancy(false);
        }
        // If pregnant, assume not planning (already achieved)
        if (isPregnant) {
            setPlanningPregnancy(false);
        }
        // If not WRA, disable planning unless explicitly checked
        // Keep planningPregnancy as is unless explicitly disabled above

    }, [sex, ageNum, isFemale, isPregnant]); // Removed isWRA dependency to allow checking planning if outside typical range but still female

    // --- Calculation Logic ---
    const calculateFolateIntake = (e) => {
        e.preventDefault();
        setShowResults(false);
        setCalculationResult(null);

        let explanation = [];
        let safetyFlags = [];
        let suggestions = { food: [], supplement: [], lifestyle: [], general: [] };
        let targetDFE = 0;
        let baseRda = 0;
        let effectiveDFE = 0;
        let totalDFE = 0;
        let syntheticFolicAcidTotalMcg = 0; // Total µg of SYNTHETIC FOLIC ACID (for UL/B12 check)

        // Validate essential inputs
        if (isNaN(ageNum) || !sex) {
            alert("Please fill in required fields: Age, Sex.");
            return;
        }

        // 1. Determine Target DFE
        const rdaKey = getRdaFolateKey(ageNum, isPregnant, isLactating);
        if (folateRdaTable[rdaKey]) {
            baseRda = folateRdaTable[rdaKey].rda;
            targetDFE = baseRda;
            explanation.push(`Base ${folateRdaTable[rdaKey].isAI ? 'AI' : 'RDA'} (${rdaKey.replace(/_/g, ' ')}): ${baseRda} µg DFE/day.`);
        } else {
            explanation.push("Could not determine base RDA. Using 400 µg DFE default.");
            targetDFE = 400; // Fallback
        }

        // Apply MTHFR adjustment
        const mthfrFactor = mthfrMultipliers[mthfrStatus] || 1.0;
        if (mthfrFactor > 1.0) {
            targetDFE = Math.round(targetDFE * mthfrFactor);
            explanation.push(`x${mthfrFactor.toFixed(1)} multiplier applied for MTHFR ${mthfrStatus} status.`);
            if (mthfrStatus === 'homo') {
                suggestions.general.push("MTHFR 677TT (homozygous): Consider discussing L-methylfolate supplements with your doctor as an alternative or addition to folic acid, as you may process folic acid less efficiently. Your target intake is also adjusted higher.");
                safetyFlags.push({ type: 'info', text: `MTHFR 677TT may benefit from methylfolate & higher target. Target adjusted x${mthfrFactor.toFixed(1)}.` });
            } else { // Hetero
                suggestions.general.push("MTHFR 677CT (heterozygous): Your target intake has been slightly increased. Ensure adequate intake through diet and potentially supplements if needed.");
                safetyFlags.push({ type: 'info', text: `MTHFR 677CT target adjusted x${mthfrFactor.toFixed(1)}.` });
            }
        }
        explanation.push(`-> Adjusted Target Intake: ${targetDFE} µg DFE/day.`);

        // NTD Prevention Rule (Applies to WRA, Planning, or Pregnant)
        let requiredFolicAcidSupplementMcg = 0;
        // Requirement applies if PREGNANT, OR (female AND within reproductive age range AND potentially planning/able to become pregnant)
        const checkNtdRequirement = isPregnant || (isFemale && ageNum >= 12 && ageNum <= 50 && planningPregnancy);
        const isPotentiallyWRA = isFemale && ageNum >= 12 && ageNum <= 50; // Define WRA for messaging

        if (isPregnant) {
            requiredFolicAcidSupplementMcg = 400; // Minimum generally recommended in prenatal
            suggestions.general.push("Pregnant: Ensure your prenatal contains at least 400 µg (up to 800 µg recommended by some guidelines) of folic acid. Total DFE goal is 600 µg.");
            safetyFlags.push({ type: 'info', text: `Pregnancy requires ≥ 600 µg DFE total, including ≥ 400 µg folic acid from supplements.` });
        } else if (planningPregnancy) {
            requiredFolicAcidSupplementMcg = 400;
            const message = `Planning Pregnancy: A daily supplement containing 400-800 µg of FOLIC ACID is recommended BEFORE conception to prevent Neural Tube Defects (NTDs), in addition to dietary folate.`;
            suggestions.general.push(message);
            safetyFlags.push({ type: 'critical', text: message }); // Critical reminder
        } else if (isPotentiallyWRA) {
            // Not pregnant, not actively planning, but is WRA - still give the general advice
            requiredFolicAcidSupplementMcg = 400; // Still note the recommendation
            const message = `Women of Reproductive Age: Even if not planning pregnancy now, a daily 400-800 µg FOLIC ACID supplement is advised to ensure adequate levels if pregnancy occurs, helping prevent Neural Tube Defects.`;
            suggestions.general.push(message);
            safetyFlags.push({ type: 'info', text: message }); // Informational reminder
        }


        // 2. Calculate Intake (Convert all to DFE)
        let diet_natural_DFE = 0;
        let diet_fortified_DFE = 0;
        let diet_fortified_folic_acid_mcg = 0; // Track synthetic folic acid for UL

        // Use parseFloat for potentially non-integer servings
        const servings = {
            leafyGreens_spinach_1cup: parseFloat(dietServings.leafyGreens_spinach_1cup) || 0,
            leafyGreens_other_1cup: parseFloat(dietServings.leafyGreens_other_1cup) || 0, // Assume 50mcg natural folate avg
            legumes_beans_peas_lentils_half_cup: parseFloat(dietServings.legumes_beans_peas_lentils_half_cup) || 0, // Assume 150mcg natural folate avg
            citrus_orange_1med: parseFloat(dietServings.citrus_orange_1med) || 0,
            avocado_half: parseFloat(dietServings.avocado_half) || 0,
            asparagus_half_cup: parseFloat(dietServings.asparagus_half_cup) || 0,
            broccoli_half_cup: parseFloat(dietServings.broccoli_half_cup) || 0,
            beef_liver_3oz: parseFloat(dietServings.beef_liver_3oz) || 0,
            // Fortified (Needs µg folic acid per serving info)
            fortified_cereal_serving: parseFloat(dietServings.fortified_cereal_serving) || 0,
            fortified_cereal_folic_acid_mcg: parseFloat(dietServings.fortified_cereal_folic_acid_mcg) || 0, // Use user input, default 0 if blank
            fortified_bread_slice: parseFloat(dietServings.fortified_bread_slice) || 0, // Use avg from foodFolateValues
            fortified_pasta_rice_cup: parseFloat(dietServings.fortified_pasta_rice_cup) || 0, // Use avg from foodFolateValues
        };

        diet_natural_DFE += servings.leafyGreens_spinach_1cup * foodFolateValues.spinach_raw_1cup * dfeFactors.natural_food;
        diet_natural_DFE += servings.leafyGreens_other_1cup * 50 * dfeFactors.natural_food; // Using avg 50mcg
        diet_natural_DFE += servings.legumes_beans_peas_lentils_half_cup * foodFolateValues.lentils_cooked_half_cup * dfeFactors.natural_food; // Using lentils as high example for average legumes
        diet_natural_DFE += servings.citrus_orange_1med * foodFolateValues.orange_medium * dfeFactors.natural_food;
        diet_natural_DFE += servings.avocado_half * foodFolateValues.avocado_half * dfeFactors.natural_food;
        diet_natural_DFE += servings.asparagus_half_cup * foodFolateValues.asparagus_cooked_half_cup * dfeFactors.natural_food;
        diet_natural_DFE += servings.broccoli_half_cup * foodFolateValues.broccoli_cooked_half_cup * dfeFactors.natural_food;
        diet_natural_DFE += servings.beef_liver_3oz * foodFolateValues.beef_liver_cooked_3oz * dfeFactors.natural_food;

        // Fortified foods - Calculate total µg of Folic Acid first
        const cereal_folic_acid_mcg = servings.fortified_cereal_serving * servings.fortified_cereal_folic_acid_mcg;
        const bread_folic_acid_mcg = servings.fortified_bread_slice * foodFolateValues.fortified_bread_slice_folic_acid;
        const pasta_rice_folic_acid_mcg = servings.fortified_pasta_rice_cup * foodFolateValues.fortified_pasta_rice_cup_folic_acid;

        diet_fortified_folic_acid_mcg = cereal_folic_acid_mcg + bread_folic_acid_mcg + pasta_rice_folic_acid_mcg;
        // Convert total dietary folic acid to DFE (assuming eaten with food)
        diet_fortified_DFE = diet_fortified_folic_acid_mcg * dfeFactors.folic_acid_with_food;

        explanation.push(`Estimated dietary intake: ${diet_natural_DFE.toFixed(0)} µg DFE from natural sources, ${diet_fortified_DFE.toFixed(0)} µg DFE from fortified foods (derived from ${diet_fortified_folic_acid_mcg.toFixed(0)} µg folic acid).`);

        // Supplement Intake
        let supplement_DFE = 0;
        let supplement_folic_acid_mcg = 0; // Track synthetic folic acid for UL from supplements

        const doseMcg = parseInt(supplementDoseMcg, 10);
        if (takesSupplement && !isNaN(doseMcg) && doseMcg > 0 && supplementFrequency) {
            let dailyFactor = 0;
            switch (supplementFrequency) {
                case 'daily':
                    dailyFactor = 1;
                    break;
                case 'alternate_day':
                    dailyFactor = 0.5;
                    break;
                case '3x_week':
                    dailyFactor = 3 / 7;
                    break;
                case 'weekly':
                    dailyFactor = 1 / 7;
                    break;
                case 'other':
                    dailyFactor = 0;
                    break;    // ← add this break
                default:
                    dailyFactor = 0;
                    break;
            }

            const avgDailyDoseMcg = doseMcg * dailyFactor;

            if (supplementForm === 'folic_acid' || supplementForm === 'prenatal') {
                // Determine DFE factor based on timing
                const factor = supplementTiming === 'empty_stomach' ? dfeFactors.folic_acid_empty_stomach : dfeFactors.folic_acid_with_food;
                supplement_DFE = avgDailyDoseMcg * factor;
                // Add the average daily µg dose of folic acid to the synthetic total
                supplement_folic_acid_mcg = avgDailyDoseMcg;
                explanation.push(`Supplement contribution (${supplementForm}): ${doseMcg} µg taken ${supplementFrequency.replace(/_/g, ' ')} (${supplementTiming.replace(/_/g, ' ')}) ≈ ${supplement_DFE.toFixed(0)} µg DFE/day.`);
            } else if (supplementForm === 'methyl_folate') {
                // Methylfolate DFE factor is less certain, using 1.7 as estimate. UL does NOT apply.
                supplement_DFE = avgDailyDoseMcg * dfeFactors.methyl_folate;
                supplement_folic_acid_mcg = 0; // Methylfolate does not count towards folic acid UL
                explanation.push(`Supplement contribution (Methylfolate): ${doseMcg} µg taken ${supplementFrequency.replace(/_/g, ' ')} ≈ ${supplement_DFE.toFixed(0)} µg DFE/day (using estimated DFE factor; does not count towards Folic Acid UL).`);
            }
        }

        // Check if required folic acid supplement for NTD prevention is met
        if (requiredFolicAcidSupplementMcg > 0 && (checkNtdRequirement || isPotentiallyWRA)) { // Check applies if pregnant, planning, or WRA
            let currentDailyFolicAcidSupplementMcg = 0;
            // Check only if taking a supplement that IS folic acid or prenatal, AND frequency is daily
            if ((supplementForm === 'folic_acid' || supplementForm === 'prenatal') && takesSupplement && !isNaN(doseMcg) && doseMcg > 0 && supplementFrequency === 'daily') {
                currentDailyFolicAcidSupplementMcg = doseMcg;
            }

            if (currentDailyFolicAcidSupplementMcg < requiredFolicAcidSupplementMcg) {
                // Tailor message based on situation
                let messagePrefix = '';
                if (isPregnant) messagePrefix = 'Pregnant Requirement';
                else if (planningPregnancy) messagePrefix = 'Planning Pregnancy Requirement';
                else messagePrefix = 'WRA Recommendation';

                const message = `${messagePrefix}: Daily ${requiredFolicAcidSupplementMcg} µg Folic Acid supplement is recommended. Your current reported daily folic acid supplement intake appears lower (${currentDailyFolicAcidSupplementMcg} µg).`;
                // Make severity higher if actually pregnant or planning
                const severity = (isPregnant || planningPregnancy) ? 'warning' : 'info';
                safetyFlags.push({ type: severity, text: message });
                suggestions.supplement.push(message + " Please review your supplement regimen or discuss with your doctor.");
            } else if (currentDailyFolicAcidSupplementMcg >= requiredFolicAcidSupplementMcg) {
                // Only add positive confirmation if actively planning or pregnant
                if (isPregnant || planningPregnancy) {
                    suggestions.supplement.push(`Your reported daily Folic Acid supplement (${currentDailyFolicAcidSupplementMcg} µg) meets the ≥${requiredFolicAcidSupplementMcg} µg recommendation for NTD prevention.`);
                }
            }
        }


        // Total DFE before absorption adjustment
        totalDFE = diet_natural_DFE + diet_fortified_DFE + supplement_DFE;
        explanation.push(`-> Total Estimated Intake (before absorption adjustment): ${totalDFE.toFixed(0)} µg DFE/day.`);

        // 3. Absorption Adjustment (applied to total DFE as per original pseudo-code structure)
        let abs_factor = 1.0;
        let penaltiesApplied = [];
        if (alcoholIntake === 'moderate') {
            abs_factor -= absorptionPenalties.alcohol_moderate;
            penaltiesApplied.push('Moderate alcohol use (-10%)');
            suggestions.lifestyle.push("Moderate alcohol intake can slightly reduce folate absorption.");
        } else if (alcoholIntake === 'heavy') {
            abs_factor -= absorptionPenalties.alcohol_heavy;
            penaltiesApplied.push('Heavy alcohol use (-20%)');
            suggestions.lifestyle.push("Heavy alcohol use significantly reduces folate absorption and increases loss. Reducing intake is advised.");
            safetyFlags.push({ type: 'warning', text: 'Heavy alcohol use impairs folate status.' });
        }

        if (hasGiCondition || takesAntiFolateMeds) {
            abs_factor -= absorptionPenalties.gi_or_med_penalty;
            let reasons = [];
            if (hasGiCondition) reasons.push("GI condition");
            if (takesAntiFolateMeds) reasons.push("Anti-folate medication");
            penaltiesApplied.push(`${reasons.join(' / ')} (-15%)`);
            suggestions.lifestyle.push(`Conditions like ${reasons.join('/')} or related medications reduce folate absorption or increase needs. Ensure adequate intake and discuss with your doctor.`);
            safetyFlags.push({ type: 'warning', text: `${reasons.join('/')} affects folate status. Medical supervision needed.` });
        }

        // Ensure absorption factor doesn't go below a reasonable minimum (e.g., 0.7 suggested previously)
        abs_factor = Math.max(abs_factor, 0.7);

        if (penaltiesApplied.length > 0) {
            explanation.push(`Absorption adjustment factor applied: ${abs_factor.toFixed(2)}x due to (${penaltiesApplied.join(', ')}).`);
            effectiveDFE = totalDFE * abs_factor;
        } else {
            explanation.push("No major absorption penalties applied.");
            effectiveDFE = totalDFE; // No adjustment needed
        }
        explanation.push(`==> Estimated EFFECTIVE intake: ${effectiveDFE.toFixed(0)} µg DFE/day.`);


        // 4. Gap & Safety Checks

        const gap = targetDFE - effectiveDFE;
        explanation.push(`Gap compared to Target: ${gap > 0 ? `${gap.toFixed(0)} µg DFE/day deficit` : `${Math.abs(gap).toFixed(0)} µg DFE/day surplus (or adequate)`}.`);

        // Calculate total SYNTHETIC Folic Acid for UL and B12 check
        // Sum of average daily µg from fortified foods and folic acid supplements
        syntheticFolicAcidTotalMcg = diet_fortified_folic_acid_mcg + supplement_folic_acid_mcg;
        explanation.push(`Estimated average daily SYNTHETIC Folic Acid intake (from fortified foods + folic acid supplements): ${syntheticFolicAcidTotalMcg.toFixed(0)} µg/day.`);

        // UL Check
        const ulKey = getUlFolateKey(ageNum, isPregnant, isLactating);
        const currentUL = folateUlTable[ulKey];
        if (currentUL !== null) {
            explanation.push(`Tolerable Upper Intake Level (UL) for SYNTHETIC Folic Acid (age ${ageNum}${isPregnant ? ', pregnant' : ''}${isLactating ? ', lactating' : ''}): ${currentUL} µg/day.`);
            if (syntheticFolicAcidTotalMcg > currentUL) {
                safetyFlags.push({ type: 'severe', text: `Your estimated intake of SYNTHETIC FOLIC ACID (~${syntheticFolicAcidTotalMcg.toFixed(0)} µg/day) EXCEEDS the Tolerable Upper Limit (UL) of ${currentUL} µg/day for your age group. This applies only to folic acid from fortified foods and supplements. Excess folic acid can mask Vitamin B12 deficiency. CONSULT YOUR DOCTOR.` });
                suggestions.supplement.push(`Reduce intake of folic acid from supplements and/or highly fortified foods to stay below the ${currentUL} µg/day UL, unless advised otherwise by your doctor.`);
            }
        } else {
            explanation.push(`UL for synthetic folic acid is not established for infants < 1 year.`);
        }

        // B12 Masking Check (Applies if synthetic folic acid > 1000 µg, regardless of age UL for adults)
        if (syntheticFolicAcidTotalMcg > 1000 && !b12StatusKnownOk) {
            safetyFlags.push({ type: 'warning', text: `High intake of SYNTHETIC FOLIC ACID (~${syntheticFolicAcidTotalMcg.toFixed(0)} µg/day) can mask the hematological signs of Vitamin B12 deficiency, potentially leading to neurological damage. Your B12 status is marked as unknown or not confirmed OK. Discuss B12 testing with your doctor.` });
            suggestions.general.push("Ensure your Vitamin B12 status is adequate, especially when taking high doses of folic acid (>1000 µg/day). Request a B12 test if status is unknown.");
        } else if (syntheticFolicAcidTotalMcg > 1000 && b12StatusKnownOk) {
            safetyFlags.push({ type: 'info', text: `Your folic acid intake is high (~${syntheticFolicAcidTotalMcg.toFixed(0)} µg/day). While your B12 status is marked OK, regular monitoring might be prudent with continued high folic acid intake.` });
        }

        // RBC Folate Context (if provided)
        const rbcLevel = parseFloat(rbcFolateLevel);
        if (!isNaN(rbcLevel)) {
            // Need units for interpretation, assuming ng/mL or nmol/L common units
            explanation.push(`Your reported RBC Folate level: ${rbcLevel.toFixed(1)} (Units not specified). This lab value provides a better assessment of long-term status than estimates.`);
            suggestions.general.push(`Your RBC Folate level (${rbcLevel.toFixed(1)}) should be interpreted by your doctor using the specific lab's reference range and your clinical context.`);
            // Example interpretation (use with caution without units):
            // if (rbcLevel > 400) explanation.push(" (Often considered replete if ng/mL)");
            // else if (rbcLevel > 900) explanation.push(" (Often considered replete if nmol/L)");
        }


        // Suggestions based on Gap
        if (gap > 0) {
            suggestions.food.push(`You are estimated to be ${gap.toFixed(0)} µg DFE short of your daily target.`);
            suggestions.food.push("Increase intake of folate-rich foods: Leafy greens (spinach, romaine), legumes (lentils, beans, peas), asparagus, broccoli, avocado, oranges.");
            suggestions.food.push("Include fortified foods like enriched breads, cereals, pasta, and rice (check labels for folic acid content).");
            // Suggest supplement if gap is significant, AND NTD requirement isn't already prompting a specific supplement need
            if (gap > 200 && requiredFolicAcidSupplementMcg === 0) { // Only suggest generic supplement if not already advised for NTD
                suggestions.supplement.push(`Consider a daily supplement providing ~${Math.min(400, Math.ceil(gap / 1.7)).toFixed(0)}-400 µg of folic acid or methylfolate to help meet your target, especially if dietary changes are difficult. Discuss with your doctor.`);
            } else if (gap > 0 && requiredFolicAcidSupplementMcg > 0) {
                suggestions.supplement.push(`Ensure your daily folic acid supplement (for NTD prevention) is sufficient to also cover this remaining estimated gap of ${gap.toFixed(0)} µg DFE.`);
            }
        } else { // Intake meets or exceeds target
            suggestions.general.push("Your estimated intake appears to meet or exceed your target DFE. Focus on maintaining a balanced diet with folate-rich foods.");
            if (syntheticFolicAcidTotalMcg > (currentUL || 1000) * 0.8) { // If close to UL
                suggestions.supplement.push("Monitor your intake of synthetic folic acid from supplements and fortified foods to ensure you don't exceed the UL.");
            }
        }

        // Top food suggestions example
        const topFoods = Object.entries(foodFolateValues)
            .filter(([key]) => !key.startsWith('fortified') && !key.includes('folic_acid')) // Show natural sources primarily
            .sort(([, valA], [, valB]) => valB - valA)
            .slice(0, 5); // Get top 5 natural sources
        suggestions.food.push(`Top natural folate sources (µg/serving): ${topFoods.map(([key, val]) => `${key.replace(/_/g, ' ').replace(' half cup', '/½ cup').replace(' 1cup', '/cup').replace(' 1med', '/med').replace(' 3oz', '/3oz')} (${val} µg)`).join(', ')}.`);


        // Set results after a short delay
        setTimeout(() => {
            setCalculationResult({
                targetDFE: targetDFE.toFixed(0),
                effectiveDFE: effectiveDFE.toFixed(0),
                gap: gap.toFixed(0),
                totalIntakePreAbsorption: totalDFE.toFixed(0),
                dietNaturalDFE: diet_natural_DFE.toFixed(0),
                dietFortifiedDFE: diet_fortified_DFE.toFixed(0),
                supplementDFE: supplement_DFE.toFixed(0),
                syntheticFolicAcidTotalMcg: syntheticFolicAcidTotalMcg.toFixed(0),
                absorptionFactor: abs_factor.toFixed(2),
                ul: currentUL,
                explanation: explanation.join('\n'),
                suggestions: suggestions,
                safetyFlags: safetyFlags,
                requiredFolicAcidSupplementMcg: requiredFolicAcidSupplementMcg, // Pass this to results for potential display logic
            });
            setShowResults(true);
        }, 300);
    };


    // --- JSX Structure ---
    return (
        <div className="vitaminFolateVitaminB9Calculator vitaminCalculatorCommon"> {/* Added common class */}
            <h1>
                <span>
                    <span role="img" aria-label="folate vitamin b9 calculator">
                        🥬
                    </span>{' '}
                    Folate (Vitamin B9) Calculator
                </span>
                <span className="ai-promo-inline">
                    <strong>Unlock Smarter Eating:</strong>{' '}
                    Get a complete breakdown of vitamins, minerals, and fats plus a personalized
                    weight-loss plan or nutrition tailored to your training with a single{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>

            <p className="important-note">
                <strong>Disclaimer:</strong> This calculator provides an ESTIMATE of your folate intake in Dietary Folate Equivalents (DFE) based on general data and your inputs. It is NOT a substitute for professional medical advice, diagnosis, or treatment. Folate needs, absorption, and metabolism vary. Consult your healthcare provider regarding your folate status, especially if you are pregnant, planning pregnancy, have health conditions (like MTHFR variants, GI disorders), take medications, or have concerns about Vitamin B12 status. High intake of SYNTHETIC FOLIC ACID can mask Vitamin B12 deficiency. Blood tests (serum folate, RBC folate, B12) provide accurate status assessment. Do not exceed the Tolerable Upper Intake Level (UL) for synthetic folic acid without medical supervision.
            </p>

            {/* --- Input Form --- */}
            <form onSubmit={calculateFolateIntake}>
                <h2>🧾 Your Information</h2>

                {/* Demographics */}
                <div className="form-section">
                    <h3>👤 Demographics & Life Stage</h3>
                    <label>
                        Age (Years):*
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 30" />
                    </label>
                    <label>
                        Sex Assigned at Birth:*
                        <select value={sex} onChange={(e) => setSex(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </select>
                    </label>
                    {isFemale && ( // Show pregnancy options only if female
                        <>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isPregnant} onChange={(e) => setIsPregnant(e.target.checked)} disabled={isLactating || ageNum < 12 || ageNum > 55} />
                                Currently Pregnant? {ageNum && (ageNum < 12 || ageNum > 55) ? <small>(Age outside typical range)</small> : ''}
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isLactating} onChange={(e) => setIsLactating(e.target.checked)} disabled={isPregnant || ageNum < 12 || ageNum > 55} />
                                Currently Lactating? {ageNum && (ageNum < 12 || ageNum > 55) ? <small>(Age outside typical range)</small> : ''}
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={planningPregnancy} onChange={(e) => setPlanningPregnancy(e.target.checked)} disabled={isPregnant} />
                                Planning Pregnancy (or could become pregnant)?
                                <small className="important-note"> Essential for NTD prevention!</small>
                            </label>
                        </>
                    )}
                    {isFemale && ageNum >= 12 && ageNum <= 50 && !planningPregnancy && !isPregnant && ( // Show WRA reminder if in age range but not planning/pregnant
                        <p className='field-note info-note'>As a woman of reproductive age, ensuring adequate folic acid intake (typically 400mcg supplement daily) is important even if not actively planning pregnancy, due to the risk of unplanned pregnancy and the need for folate early in gestation.</p>
                    )}
                </div>

                {/* Genetics */}
                <div className="form-section">
                    <h3>🧬 Genetics (Optional)</h3>
                    <label>
                        MTHFR C677T Status (if known):
                        <select value={mthfrStatus} onChange={(e) => setMthfrStatus(e.target.value)}>
                            <option value="none">Unknown / Normal (CC)</option>
                            <option value="hetero">Heterozygous (CT)</option>
                            <option value="homo">Homozygous (TT)</option>
                        </select>
                        <small>The 677TT genotype may affect folic acid metabolism and increase needs. Consult your doctor if known.</small>
                    </label>
                </div>

                {/* Diet Diary (Daily Servings) */}
                <div className="form-section">
                    <h3>🥗 Dietary Intake (Average DAILY Servings)</h3>
                    <small>Estimate your average number of servings consumed PER DAY.</small>
                    <h4>Natural Folate Sources:</h4>
                    <div className="diet-grid">
                        <label>
                            Leafy Greens (e.g., 1 cup raw Spinach):
                            <input type="number" name="leafyGreens_spinach_1cup" value={dietServings.leafyGreens_spinach_1cup} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 1" />
                            <small>(~{foodFolateValues.spinach_raw_1cup} µg / cup raw)</small>
                        </label>
                        <label>
                            Other Leafy Greens (e.g., 1 cup Romaine):
                            <input type="number" name="leafyGreens_other_1cup" value={dietServings.leafyGreens_other_1cup} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 0.5" />
                            <small>(~50 µg / cup avg)</small>
                        </label>
                        <label>
                            Legumes (Beans, Peas, Lentils, ½ cup cooked):
                            <input type="number" name="legumes_beans_peas_lentils_half_cup" value={dietServings.legumes_beans_peas_lentils_half_cup} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 0.5" />
                            <small>(~105-180 µg / ½ cup)</small>
                        </label>
                        <label>
                            Asparagus (½ cup cooked):
                            <input type="number" name="asparagus_half_cup" value={dietServings.asparagus_half_cup} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 0" />
                            <small>(~{foodFolateValues.asparagus_cooked_half_cup} µg / ½ cup)</small>
                        </label>
                        <label>
                            Broccoli (½ cup cooked):
                            <input type="number" name="broccoli_half_cup" value={dietServings.broccoli_half_cup} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 1" />
                            <small>(~{foodFolateValues.broccoli_cooked_half_cup} µg / ½ cup)</small>
                        </label>
                        <label>
                            Avocado (½ medium):
                            <input type="number" name="avocado_half" value={dietServings.avocado_half} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 0.5" />
                            <small>(~{foodFolateValues.avocado_half} µg / half)</small>
                        </label>
                        <label>
                            Orange (1 medium):
                            <input type="number" name="citrus_orange_1med" value={dietServings.citrus_orange_1med} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 1" />
                            <small>(~{foodFolateValues.orange_medium} µg / med)</small>
                        </label>
                        <label>
                            Beef Liver (3 oz cooked - if consumed):
                            <input type="number" name="beef_liver_3oz" value={dietServings.beef_liver_3oz} onChange={handleDietChange} min="0" step="0.1" placeholder="e.g., 0.1 (if weekly)" />
                            <small>(~{foodFolateValues.beef_liver_cooked_3oz} µg / 3 oz - Very High)</small>
                        </label>
                    </div>
                    <h4>Fortified Foods (Contain Folic Acid):</h4>
                    <div className="diet-grid">
                        <label>
                            Fortified Breakfast Cereal (Servings/day):
                            <input type="number" name="fortified_cereal_serving" value={dietServings.fortified_cereal_serving} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 1" />
                        </label>
                        <label>
                            Folic Acid per Cereal Serving (µg):*
                            <input type="number" name="fortified_cereal_folic_acid_mcg" value={dietServings.fortified_cereal_folic_acid_mcg} onChange={handleDietChange} min="0" placeholder="Check label (e.g., 100, 400)" />
                            <small>REQUIRED if eating fortified cereal.</small>
                        </label>
                        <label>
                            Fortified Bread (Slices/day):
                            <input type="number" name="fortified_bread_slice" value={dietServings.fortified_bread_slice} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 2" />
                            <small>(~{foodFolateValues.fortified_bread_slice_folic_acid} µg folic acid / slice avg)</small>
                        </label>
                        <label>
                            Fortified Pasta/Rice (Cooked cups/day):
                            <input type="number" name="fortified_pasta_rice_cup" value={dietServings.fortified_pasta_rice_cup} onChange={handleDietChange} min="0" step="0.5" placeholder="e.g., 0.5" />
                            <small>(~{foodFolateValues.fortified_pasta_rice_cup_folic_acid} µg folic acid / cup avg)</small>
                        </label>
                    </div>
                </div>

                {/* Supplement Use */}
                <div className="form-section">
                    <h3>💊 Supplement Use</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesSupplement} onChange={(e) => setTakesSupplement(e.target.checked)} />
                        Do you currently take a folate supplement (including prenatal)?
                    </label>
                    {takesSupplement && (
                        <div className='supplement-details'>
                            <label>
                                Form:*
                                <select value={supplementForm} onChange={(e) => setSupplementForm(e.target.value)} required={takesSupplement}>
                                    <option value="folic_acid">Folic Acid</option>
                                    <option value="methyl_folate">L-Methylfolate (5-MTHF)</option>
                                    <option value="prenatal">Prenatal Vitamin (usually Folic Acid)</option>
                                </select>
                                <small>Check label. Folic acid is synthetic. Methylfolate is active form.</small>
                            </label>
                            <label>
                                Dose per intake (µg - micrograms):*
                                <input type="number" value={supplementDoseMcg} onChange={(e) => setSupplementDoseMcg(e.target.value)} required={takesSupplement} min="1" placeholder="e.g., 400, 600, 800, 1000" />
                            </label>
                            <label>
                                Frequency:*
                                <select value={supplementFrequency} onChange={(e) => setSupplementFrequency(e.target.value)} required={takesSupplement}>
                                    <option value="">-- Select --</option>
                                    <option value="daily">Daily</option>
                                    <option value="alternate_day">Alternate Days</option>
                                    <option value="3x_week">3 times per week</option>
                                    <option value="weekly">Once per week</option>
                                    <option value="other">Other / Irregular</option>
                                </select>
                            </label>
                            <label>
                                Timing (for Folic Acid):
                                <select value={supplementTiming} onChange={(e) => setSupplementTiming(e.target.value)} disabled={supplementForm === 'methyl_folate'}>
                                    <option value="with_food">With Food</option>
                                    <option value="empty_stomach">Empty Stomach</option>
                                </select>
                                <small>Folic acid absorbed better on empty stomach (DFE x2.0 vs x1.7). Less critical for methylfolate.</small>
                            </label>
                        </div>
                    )}
                </div>

                {/* Lifestyle & Health Factors */}
                <div className="form-section">
                    <h3>🩺 Lifestyle & Health Factors</h3>
                    <label>
                        Alcohol Intake:
                        <select value={alcoholIntake} onChange={(e) => setAlcoholIntake(e.target.value)}>
                            <option value="none">None / Rarely</option>
                            <option value="moderate">Moderate (e.g., up to 1 drink/day women, 2/day men)</option>
                            <option value="heavy">Heavy (Exceeds moderate)</option>
                        </select>
                        <small>Alcohol can impair folate absorption and increase needs.</small>
                    </label>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={hasGiCondition} onChange={(e) => setHasGiCondition(e.target.checked)} />
                        Do you have a malabsorption condition (IBD, Celiac, Bariatric Surgery)?
                        <small> May decrease absorption.</small>
                    </label>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesAntiFolateMeds} onChange={(e) => setTakesAntiFolateMeds(e.target.checked)} />
                        Do you take medications known to interfere with folate (e.g., Methotrexate, certain anti-epileptics like Valproate, Phenytoin)?
                        <small> Consult doctor; may increase needs or require specific forms.</small>
                    </label>
                </div>

                {/* Labs (Optional) */}
                <div className="form-section">
                    <h3>🩸 Lab Status (Optional & Important Context)</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={b12StatusKnownOk} onChange={(e) => setB12StatusKnownOk(e.target.checked)} />
                        Has your Vitamin B12 status been checked and confirmed to be adequate recently?
                        <small className="important-note"> Crucial if taking {'>'} 1000 µg/day of Folic Acid.</small>
                    </label>
                    {!b12StatusKnownOk && (
                        <p className='field-note warning-note'>If B12 status is unknown or low, high folic acid intake ({'>'}1000 µg/day) can mask B12 deficiency anemia, risking neurological damage. Discuss B12 testing with your doctor if taking high-dose folic acid.</p>
                    )}
                    <label>
                        RBC Folate Level (if known):
                        <input type="number" value={rbcFolateLevel} onChange={(e) => setRbcFolateLevel(e.target.value)} min="0" step="any" placeholder="e.g., 600 ng/mL or 1360 nmol/L" />
                        <small>Enter if known (include units if possible). Provides measure of long-term stores.</small>
                    </label>
                </div>


                <button type="submit">Calculate Folate (B9) Status</button>
            </form>

            {/* --- Results Display --- */}
            {showResults && calculationResult && (
                <div className="results-section">
                    <h2>📊 Estimated Folate (B9) Status</h2>
                    {/* Safety Flags Display */}
                    {calculationResult.safetyFlags.length > 0 && (
                        <div className="safety-alerts">
                            <h4>⚠️ Safety Alerts & Important Considerations</h4>
                            <ul>
                                {calculationResult.safetyFlags.map((flag, index) => (
                                    <li key={index} className={`alert-${flag.type}`}>
                                        <strong>{flag.type === 'critical' ? 'CRITICAL' : flag.type.toUpperCase()}:</strong> {flag.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Gauge Placeholder */}
                    <div className="result-gauge-placeholder">
                        {/* Simple Bar Gauge */}
                        <div className="gauge-container">
                            <div className="gauge-bar-folate" style={{ width: `${Math.min(100, (calculationResult.effectiveDFE / calculationResult.targetDFE) * 100)}%` }} title={`Effective Intake: ${calculationResult.effectiveDFE} µg DFE`}>
                                <span>{calculationResult.effectiveDFE} µg DFE</span>
                            </div>
                            <div className="gauge-target-marker" style={{ left: `100%` }} title={`Target: ${calculationResult.targetDFE} µg DFE`}>
                                <span>Target: {calculationResult.targetDFE} µg DFE</span>
                            </div>
                        </div>
                        {/* <span className="gauge-label">Effective Intake vs. Target</span> */}
                    </div>


                    <div className="results-grid">
                        <div className="result-item">
                            <span className="result-label">Personalized Target:</span>
                            <span className="result-value">{calculationResult.targetDFE} µg DFE/day</span>
                            <small className='result-note'>(Based on RDA/AI, adjusted for MTHFR if applicable)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Effective Intake:</span>
                            <span className="result-value significant">{calculationResult.effectiveDFE} µg DFE/day</span>
                            <small className='result-note'>(Total DFE adjusted for absorption factors: {calculationResult.absorptionFactor}x)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Intake vs Target:</span>
                            <span className={`result-value status-${calculationResult.gap > calculationResult.targetDFE * 0.5 ? 'low' : calculationResult.gap > 0 ? 'borderline' : 'adequate'}`}>
                                {calculationResult.gap > 0
                                    ? `Deficit (~${calculationResult.gap} µg DFE)`
                                    : `Adequate / Surplus`
                                }
                            </span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Natural Food Folate:</span>
                            <span className="result-value">{calculationResult.dietNaturalDFE} µg DFE/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Fortified Food Folate:</span>
                            <span className="result-value">{calculationResult.dietFortifiedDFE} µg DFE/day</span>
                            <small className='result-note'>(From ~{calculationResult.syntheticFolicAcidTotalMcg - calculationResult.supplementDFE > 0 ? (calculationResult.syntheticFolicAcidTotalMcg - calculationResult.supplementDFE / (calculationResult.supplementForm === 'folic_acid' || calculationResult.supplementForm === 'prenatal' ? (calculationResult.supplementTiming === 'empty_stomach' ? 2.0 : 1.7) : 0)).toFixed(0) : 0} µg Folic Acid)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Supplement Folate:</span>
                            <span className="result-value">{calculationResult.supplementDFE} µg DFE/day</span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. SYNTHETIC Folic Acid:</span>
                            <span className="result-value">{calculationResult.syntheticFolicAcidTotalMcg} µg/day</span>
                            <small className='result-note'>(Fortified Food + Folic Acid Supps - Compare to UL)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Folic Acid UL:</span>
                            <span className="result-value">
                                {calculationResult.ul !== null ? `${calculationResult.ul} µg/day` : 'N/A (<1y)'}
                            </span>
                            <small className='result-note'>(Max safe daily SYNTHETIC folic acid)</small>
                        </div>
                        {calculationResult.requiredFolicAcidSupplementMcg > 0 && (isPregnant || planningPregnancy) && ( // Only show explicit requirement if pregnant/planning
                            <div className="result-item">
                                <span className="result-label">Folic Acid Supplement Req:</span>
                                <span className="result-value">{calculationResult.requiredFolicAcidSupplementMcg} µg/day</span>
                                <small className='result-note'>(For NTD Prevention)</small>
                            </div>
                        )}
                    </div>

                    <h3>💡 Guidance & Suggestions</h3>
                    {/* General / NTD Reminders */}
                    {calculationResult.suggestions.general.length > 0 && (
                        <div className="suggestions-box">
                            <h4>📌 General Recommendations:</h4>
                            <ul>{calculationResult.suggestions.general.map((s, i) => <li key={`gen-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {/* Gap Statement */}
                    <p className="gap-statement">
                        {calculationResult.gap > 0
                            ? `You appear to be about ${calculationResult.gap} µg DFE short of your personalized daily target.`
                            : `Your estimated effective intake meets or exceeds your personalized daily target.`
                        }
                    </p>
                    {/* Suggestions Sections */}
                    {calculationResult.suggestions.food.length > 0 && (
                        <div className="suggestions-box">
                            <h4>🥗 Food Suggestions:</h4>
                            <ul>{calculationResult.suggestions.food.map((s, i) => <li key={`food-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.supplement.length > 0 && (
                        <div className="suggestions-box">
                            <h4>💊 Supplement Considerations:</h4>
                            <ul>{calculationResult.suggestions.supplement.map((s, i) => <li key={`supp-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}
                    {calculationResult.suggestions.lifestyle.length > 0 && (
                        <div className="suggestions-box">
                            <h4>⚖️ Lifestyle Factors:</h4>
                            <ul>{calculationResult.suggestions.lifestyle.map((s, i) => <li key={`life-${i}`}>{s}</li>)}</ul>
                        </div>
                    )}

                    <h3>📝 Calculation Explanation</h3>
                    <pre className="explanation-box">{calculationResult.explanation}</pre>

                    <p className="important-note">
                        <strong>Reminder:</strong> This is an educational tool, not medical advice. Estimates depend on the accuracy of your input and average food values. Individual needs and absorption vary. Discuss these estimates and any planned changes (especially supplement use) with your healthcare provider, considering your specific health status, lab results, and pregnancy plans.
                    </p>
                    <a href="#folate-education" className="scroll-link">Learn more about Folate (B9) below</a>
                </div>
            )}

            {/* --- Educational Content (Folate specific) --- */}
            <div id="folate-education" className="educational-section">
                <h2>Understanding Folate (Vitamin B9)</h2>

                {/* Content Block 1: Fundamentals */}
                <div className="content-block">
                    <h3>1. Folate Fundamentals (Biology & Safety)</h3>
                    <dl>
                        <dt>Physiological Roles:</dt>
                        <dd>Essential B-vitamin acting as a coenzyme in single-carbon transfers. Critical for:
                            <ul>
                                <li>DNA and RNA synthesis and repair.</li>
                                <li>Amino acid metabolism (e.g., homocysteine to methionine).</li>
                                <li>Formation of red and white blood cells.</li>
                                <li>Rapid cell division and growth, especially during pregnancy and infancy.</li>
                            </ul>
                        </dd>

                        <dt>Chemical Forms & DFE:</dt>
                        <dd>
                            <ul>
                                <li><strong>Food Folate:</strong> Naturally occurring forms (reduced polyglutamates) found in foods like leafy greens, legumes, liver. Bioavailability varies.</li>
                                <li><strong>Folic Acid:</strong> Synthetic, oxidized monoglutamate form used in fortified foods and most supplements. More stable and generally more bioavailable than food folate.</li>
                                <li><strong>L-Methylfolate (5-MTHF):</strong> A biologically active form available in some supplements, potentially beneficial for individuals with MTHFR polymorphisms as it bypasses certain metabolic steps.</li>
                                <li><strong>Dietary Folate Equivalents (DFE):</strong> Accounts for bioavailability differences. Calculator uses these factors:
                                    <ul>
                                        <li>1 µg natural food folate = 1 DFE</li>
                                        <li>1 µg folic acid (with food/fortified) = 1.7 DFE</li>
                                        <li>1 µg folic acid (supplement on empty stomach) = 2.0 DFE</li>
                                        <li>(Methylfolate DFE often estimated as 1.7, but not officially defined by IOM)</li>
                                    </ul>
                                </li>
                            </ul>
                        </dd>

                        <dt>Neural Tube Defect (NTD) Prevention:</dt>
                        <dd>Adequate folate intake BEFORE and during early pregnancy is crucial for preventing NTDs (e.g., spina bifida, anencephaly). Supplementation with <strong>400-800 µg/day of FOLIC ACID</strong> for all women who could become pregnant significantly reduces NTD risk (by ≥ 50%). This should start at least 1 month before conception and continue through the first trimester.</dd>

                        <dt>MTHFR Variant:</dt>
                        <dd>The MTHFR gene provides instructions for an enzyme involved in folate metabolism. The C677T polymorphism is common. Individuals with the <strong>677TT genotype (homozygous)</strong> may process folic acid less efficiently, potentially leading to lower serum folate and higher homocysteine levels. They might benefit from higher folate intake or using the active L-methylfolate form. Discuss with a doctor if genotype is known.</dd>

                        <dt>UL (Tolerable Upper Intake Level):</dt>
                        <dd>Applies <strong>ONLY to synthetic folic acid</strong> intake from fortified foods and supplements. It does NOT apply to natural food folate or L-methylfolate. The UL exists because high folic acid intake can mask the anemia associated with Vitamin B12 deficiency, allowing neurological damage to progress undetected.
                            <ul>
                                <li>Adults (19+): 1,000 µg/day</li>
                                <li>Adolescents (14-18y): 800 µg/day</li>
                                <li>Children: Scaled lower (see table below).</li>
                            </ul>
                        </dd>
                        <dt>Vitamin B12 Interaction:</dt>
                        <dd>Crucial interaction. Both folate and B12 are needed for cell division. Deficiency in either can cause megaloblastic anemia. High folic acid intake can correct this anemia even if B12 is deficient, but it does NOT correct the underlying B12 deficiency, which can lead to irreversible nerve damage. <strong>It is vital to ensure B12 adequacy before starting high-dose folic acid supplementation ({'>'}1000 µg/day).</strong></dd>
                    </dl>
                </div>

                {/* Content Block 2: RDAs */}
                <div className="content-block">
                    <h3>2. Reference Intakes (RDA / AI) - IOM DRIs</h3>
                    <p>Expressed as µg Dietary Folate Equivalents (DFE) per day.</p>
                    <table>
                        <thead>
                            <tr><th>Life Stage</th><th>RDA / AI (µg DFE/day)</th></tr>
                        </thead>
                        <tbody>
                            <tr><td>0–6 months †</td><td>65</td></tr>
                            <tr><td>7–12 months †</td><td>80</td></tr>
                            <tr><td>1–3 years</td><td>150</td></tr>
                            <tr><td>4–8 years</td><td>200</td></tr>
                            <tr><td>9–13 years</td><td>300</td></tr>
                            <tr><td>14+ years</td><td>400</td></tr>
                            <tr><td>Pregnant</td><td>600</td></tr>
                            <tr><td>Lactating</td><td>500</td></tr>
                        </tbody>
                    </table>
                    <small>† Adequate Intake (AI) values for infants.</small>
                    <p><strong>Note on Pregnancy/Planning:</strong> While the RDA for pregnancy is 600 µg DFE, the recommendation for NTD prevention specifically calls for 400-800 µg of supplemental FOLIC ACID daily for all women capable of becoming pregnant.</p>
                </div>

                {/* Content Block 3: High Risk Groups */}
                <div className="content-block">
                    <h3>3. Groups to Highlight / Increased Needs</h3>
                    <ul>
                        <li><strong>Women of Reproductive Age & Those Planning Pregnancy:</strong> Highest priority due to NTD risk. Require ≥ 400 µg/day supplemental folic acid BEFORE conception.</li>
                        <li><strong>Pregnant Women:</strong> Increased needs (600 µg DFE/day) for fetal development. Prenatal vitamins typically provide 400-800 µg folic acid.</li>
                        <li><strong>MTHFR 677TT Homozygotes:</strong> May have increased needs or benefit from L-methylfolate. Requires medical guidance.</li>
                        <li><strong>Alcohol Use Disorder:</strong> Chronic alcohol intake impairs folate absorption and increases urinary excretion.</li>
                        <li><strong>Malabsorption Syndromes:</strong> Conditions like Celiac disease, Inflammatory Bowel Disease (IBD), or history of bariatric surgery can significantly reduce folate absorption.</li>
                        <li><strong>Certain Medications:</strong> Anti-folate drugs (e.g., Methotrexate used for cancer/autoimmune diseases) directly interfere with folate metabolism. Some anti-epileptic drugs (e.g., phenytoin, valproate, lamotrigine) can also lower folate levels. Requires close medical management.</li>
                        <li><strong>Individuals with Untreated Vitamin B12 Deficiency:</strong> High folic acid intake is risky due to masking potential. B12 status should be assessed.</li>
                    </ul>
                </div>

                {/* --- Sources Section (Folate specific) --- */}
                <div className="content-block">
                    <h3>Sources</h3>
                    <p className="sources">
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Folate Fact Sheet for Health Professionals*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/Folate-HealthProfessional/ <br />
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Folate Fact Sheet for Consumers*. Retrieved [April-14-2025], from https://ods.od.nih.gov/factsheets/Folate-Consumer/ <br />
                        Institute of Medicine (US) Standing Committee on the Scientific Evaluation of Dietary Reference Intakes and its Panel on Folate, Other B Vitamins, and Choline. (1998). *Dietary Reference Intakes for Thiamin, Riboflavin, Niacin, Vitamin B6, Folate, Vitamin B12, Pantothenic Acid, Biotin, and Choline*. The National Academies Press. https://doi.org/10.17226/6015 (Primary DRI source) <br />
                        U.S. Preventive Services Task Force (USPSTF). (2017). Folic Acid Supplementation for the Prevention of Neural Tube Defects: US Preventive Services Task Force Recommendation Statement. *JAMA*, *317*(2), 183–189. https://doi.org/10.1001/jama.2016.19438 (NTD Prevention Guideline) <br />
                        Moll, S., & Varga, E. A. (2015). Homocysteine and MTHFR Mutations. *Circulation*, *132*(1), e4-e5. (Brief overview of MTHFR) <br />
                        Crider, K. S., Yang, T. P., Berry, R. J., & Bailey, L. B. (2012). Folate and vitamin B-12 status in relation to ridiculopathy among U.S. adults. *Nutrition & Metabolism*, *9*, 13. https://doi.org/10.1186/1743-7075-9-13 (Example reference for B12 interaction/masking discussion) <br />
                        USDA FoodData Central. (n.d.). Retrieved [April-14-2025], from https://fdc.nal.usda.gov/ (For specific food values) <br />
                        EatingWell, Healthline, Medical News Today (General nutrition sites for cross-referencing common food values - specific links might be needed if values differ significantly from USDA/ODS).
                    </p>
                    {/* Remember to add actual Retrieval Dates */}
                </div>
                {/* --- END OF SOURCES SECTION --- */}

            </div> {/* End educational-section */}
        </div> // End vitaminFolateVitaminB9Calculator
    );
}

export default VitaminFolateVitaminB9Calculator;